import {
  fetchKioskConfig,
  isKioskConfig,
  KioskPunchError,
} from "@/api/punch";
import { KIOSK_DEFAULT_LOCALE, type LocaleCode } from "@/i18n";

export interface KioskReadyBootstrapState {
  readonly status: "ready";
  readonly locale: LocaleCode;
  readonly locationLabel: string;
}

export interface KioskBlockedBootstrapState {
  readonly status: "unprovisioned" | "disabled" | "unavailable";
  readonly locale: LocaleCode;
  readonly locationLabel: null;
}

export type KioskBootstrapState =
  | KioskReadyBootstrapState
  | KioskBlockedBootstrapState;

type ConfigFetcher = () => Promise<unknown>;

const UNPROVISIONED_MESSAGE_KEYS = new Set([
  "errors.code.kiosk.device_token_invalid",
  "errors.code.kiosk.device_auth_invalid",
  "errors.code.device.revoked",
  "errors.code.device.kiosk_configuration_incomplete",
]);

function blockedStatusFrom(error: unknown): KioskBlockedBootstrapState["status"] {
  if (!(error instanceof KioskPunchError)) return "unavailable";
  if (error.messageKey === "errors.code.time.source_not_enabled") {
    return "disabled";
  }
  if (
    error.classification === "authentication" ||
    UNPROVISIONED_MESSAGE_KEYS.has(error.messageKey)
  ) {
    return "unprovisioned";
  }
  return "unavailable";
}

/** Config failures render an explicit fail-closed state, never a ready shell. */
export async function resolveKioskBootstrap(
  fetchConfig: ConfigFetcher = fetchKioskConfig,
): Promise<KioskBootstrapState> {
  try {
    const config = await fetchConfig();
    if (!isKioskConfig(config)) {
      throw new KioskPunchError(
        "errors.code.platform.unhandled_error",
        undefined,
        "internal",
      );
    }
    return {
      status: "ready",
      locale: config.locale,
      locationLabel: config.location_label,
    };
  } catch (error) {
    // No log: a lower-level failure may have involved a device credential.
    return {
      status: blockedStatusFrom(error),
      locale: KIOSK_DEFAULT_LOCALE,
      locationLabel: null,
    };
  }
}
